import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './ConfirmDialog.css';

function ConfirmDialog({
  isOpen,
  message,
  onConfirm,
  onCancel,
  buttonPosition = null,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  // When set, the dialog shows an optional comment box under the message with
  // this as its label, and onConfirm receives the text typed.
  commentLabel = null
}) {
  const dialogRef = useRef(null);
  const savedScrollPosition = useRef({ x: 0, y: 0 });
  const [comment, setComment] = useState('');

  // Each opening starts with an empty box.
  useEffect(() => {
    if (isOpen) setComment('');
  }, [isOpen]);

  // Position, lock the page and focus — all BEFORE the first paint (useLayoutEffect).
  //
  // The dialog is position: fixed, so its coordinates are viewport coordinates
  // and the page's scroll offset never enters into it. It used to be absolute,
  // positioned in a normal effect: React mounted it at the foot of the page
  // (where it sits in the JSX), the comment box's autoFocus scrolled the window
  // down to it, and only then was it moved "near the button" — using the
  // already-jumped scroll offset — while the close handler restored that jumped
  // position too. Net effect: Reject / Offer Declined threw the page to the
  // bottom and left it there (2026-10-09).
  const wasOpen = useRef(false);
  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    wasOpen.current = true;
    savedScrollPosition.current = {
      x: window.scrollX || window.pageXOffset,
      y: window.scrollY || window.pageYOffset
    };
    document.body.style.overflow = 'hidden';

    const dialog = dialogRef.current;
    if (dialog) {
      const rect = dialog.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let top;
      let left;
      if (buttonPosition) {
        // Just under the button; above it if that would run off the bottom.
        top = buttonPosition.bottom + 10;
        left = buttonPosition.left;
        if (top + rect.height > vh - 20) top = buttonPosition.top - rect.height - 10;
      } else {
        top = (vh - rect.height) / 2;
        left = (vw - rect.width) / 2;
      }
      // Whatever happens, stay inside the viewport (a button that has scrolled
      // off-screen must not take the dialog with it).
      top = Math.min(Math.max(top, 20), Math.max(20, vh - rect.height - 20));
      left = Math.min(Math.max(left, 20), Math.max(20, vw - rect.width - 20));
      dialog.style.top = `${top}px`;
      dialog.style.left = `${left}px`;

      // Focus without scrolling: the comment box if there is one, else Cancel.
      const field = dialog.querySelector('textarea') || dialog.querySelector('.btn-cancel');
      if (field) field.focus({ preventScroll: true });
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, buttonPosition]);

  // On close, put the page back where it was when the dialog opened. The
  // dialog never moves it now, so this is belt and braces. Skipped on mount,
  // when there is nothing to restore (it used to scroll to 0,0).
  useEffect(() => {
    if (isOpen || !wasOpen.current) return;
    requestAnimationFrame(() => {
      window.scrollTo(savedScrollPosition.current.x, savedScrollPosition.current.y);
    });
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div className="confirm-overlay" onClick={onCancel} />
      <div className="confirm-dialog" ref={dialogRef}>
        <div className="confirm-message">
          {message.split('\n').map((line, index) => (
            <React.Fragment key={index}>
              {line}
              {index < message.split('\n').length - 1 && <br />}
            </React.Fragment>
          ))}
        </div>
        {commentLabel && (
          <div className="confirm-comment">
            <label htmlFor="confirm-comment-input">{commentLabel}</label>
            <textarea
              id="confirm-comment-input"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
        )}
        <div className="confirm-buttons">
          <button
            className="btn-cancel"
            onClick={onCancel}
          >
            {cancelText}
          </button>
          <button
            className="btn-confirm"
            onClick={() => onConfirm(comment)}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </>
  );
}

export default ConfirmDialog;
